const BASE = "/api";

function getToken(): string | null {
  return localStorage.getItem("caprina_token");
}

export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
  const { headers: optHeaders, ...restOptions } = options ?? {};
  const res = await fetch(`${BASE}${path}`, {
    ...restOptions,
    headers: { "Content-Type": "application/json", ...authHeader, ...(optHeaders as Record<string, string> | undefined) },
  });
  if (res.status === 204) return undefined as unknown as T;
  if (res.status === 401) {
    // dispatch event — AuthContext هو المسؤول عن الـ logout وليس apiFetch مباشرة
    // ملاحظة: new CustomEvent() ممكن يرمي TypeError: Illegal constructor في بعض WebView/PWA contexts،
    // فبنعمل fallback آمن عشان مانكسرش الـ flow كله بسبب الـ event ده
    try {
      window.dispatchEvent(new CustomEvent("caprina:unauthorized"));
    } catch {
      try {
        const evt = document.createEvent("Event");
        evt.initEvent("caprina:unauthorized", false, false);
        window.dispatchEvent(evt);
      } catch {
        // تجاهل — التوكن لسه هيتمسح والخطأ تحت هيتعامل معاه الكولر
      }
    }
    throw new Error("غير مصرح");
  }
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`) as Error & Record<string, unknown>;
    // نلحق أي حقول إضافية راجعة من الباك إند (زي flagged) بالـ Error عشان الكولر يقدر يتصرف بناءً عليها
    if (data && typeof data === "object") {
      for (const key of Object.keys(data)) {
        if (key !== "error") (err as any)[key] = (data as any)[key];
      }
    }
    throw err;
  }
  return data as T;
}

// ─── Auth API ──────────────────────────────────────────────────────────────
export interface LoginResponse {
  token: string;
  user: {
    id: number; username: string; displayName: string;
    role: "admin" | "employee" | "warehouse";
    permissions: string[]; isActive: boolean;
    createdAt: string; updatedAt: string;
  };
}

export const authApi = {
  login: (username: string, password: string) =>
    apiFetch<LoginResponse>("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  me: () => apiFetch<LoginResponse["user"]>("/auth/me"),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<{ success: boolean }>("/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
  updateProfile: (data: { avatar?: string | null; displayName?: string }) =>
    apiFetch<LoginResponse["user"]>("/auth/update-profile", { method: "PATCH", body: JSON.stringify(data) }),
};

// ─── Users API ─────────────────────────────────────────────────────────────
export interface AppUser {
  id: number; username: string; displayName: string;
  role: "admin" | "employee" | "warehouse";
  permissions: string[]; isActive: boolean;
  jobTitle?: string | null;
  department?: string | null;
  avatar?: string | null;
  showProfileLink?: boolean;
  createdAt: string; updatedAt: string;
}

function parseUserPermissions(u: any): AppUser {
  const perms = u.permissions;
  return {
    ...u,
    permissions: Array.isArray(perms)
      ? perms
      : typeof perms === "string"
        ? (() => { try { return JSON.parse(perms); } catch { return []; } })()
        : [],
  };
}

export const usersApi = {
  list: () => apiFetch<AppUser[]>("/users").then(arr => arr.map(parseUserPermissions)),
  create: (data: { username: string; password: string; displayName: string; role: string; permissions?: string[] }) =>
    apiFetch<AppUser>("/users", { method: "POST", body: JSON.stringify(data) }).then(parseUserPermissions),
  update: (id: number, data: Partial<{ displayName: string; role: string; permissions: string[]; isActive: boolean; password: string }>) =>
    apiFetch<AppUser>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(data) }).then(parseUserPermissions),
  delete: (id: number) => apiFetch<void>(`/users/${id}`, { method: "DELETE" }),
};

// ─── Audit Logs API ────────────────────────────────────────────────────────
export interface AuditLogEntry {
  id: number; action: string; entityType: string;
  entityId: number | null; entityName: string | null;
  changesBefore: Record<string, unknown> | null;
  changesAfter: Record<string, unknown> | null;
  userId: number | null; userName: string | null;
  createdAt: string;
}

export const auditApi = {
  list: (params?: { entityType?: string; action?: string; search?: string; limit?: number; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.entityType) q.set("entityType", params.entityType);
    if (params?.action) q.set("action", params.action);
    if (params?.search) q.set("search", params.search);
    if (params?.limit) q.set("limit", String(params.limit));
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    return apiFetch<AuditLogEntry[]>(`/audit-logs?${q.toString()}`);
  },
};

export interface Product {
  id: number;
  name: string;
  sku: string | null;
  totalQuantity: number;
  reservedQuantity: number;
  soldQuantity: number;
  lowStockThreshold: number;
  unitPrice: number;
  costPrice: number | null;
  image: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductVariant {
  id: number;
  productId: number;
  productName?: string;
  color: string;
  colorHex?: string | null;
  size: string;
  sku: string | null;
  totalQuantity: number;
  reservedQuantity: number;
  soldQuantity: number;
  lowStockThreshold: number;
  unitPrice: number;
  costPrice: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShippingCompany {
  id: number;
  name: string;
  phone: string | null;
  website: string | null;
  notes: string | null;
  logo: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface ParsedImport {
  headers: string[];
  sample: any[][];
  totalRows: number;
  allRows: any[][];
}

export interface ColumnMapping {
  name: string;
  phone: string;
  city: string;
  address: string;
  product: string;
  color: string;
  size: string;
  quantity: string;
  price: string;
  notes: string;
  adSource: string;
  warehouseId: string;
  assignedUserId: string;
  shippingCost: string;
}

export interface ImportResult {
  imported: number;
  failed: number;
  errors: string[];
  orders: any[];
}

export const productsApi = {
  list: () => apiFetch<Product[]>("/products"),
  create: (data: Partial<Product> & { name: string }) => apiFetch<Product>("/products", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<Omit<Product, "totalQuantity">>) => apiFetch<Product>(`/products/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => apiFetch<void>(`/products/${id}`, { method: "DELETE" }),
  addStock: (id: number, quantity: number, notes?: string | null) =>
    apiFetch<Product>(`/products/${id}/add-stock`, { method: "POST", body: JSON.stringify({ quantity, notes }) }),
};

export const variantsApi = {
  listAll: () => apiFetch<ProductVariant[]>("/variants"),
  listByProduct: (productId: number) => apiFetch<ProductVariant[]>(`/products/${productId}/variants`),
  create: (productId: number, data: { color: string; colorHex?: string | null; size: string; sku?: string; totalQuantity?: number; lowStockThreshold: number; unitPrice: number; costPrice?: number | null }) =>
    apiFetch<ProductVariant>(`/products/${productId}/variants`, { method: "POST", body: JSON.stringify(data) }),
  update: (productId: number, variantId: number, data: Partial<Omit<ProductVariant, "totalQuantity">>) =>
    apiFetch<ProductVariant>(`/products/${productId}/variants/${variantId}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (productId: number, variantId: number) =>
    apiFetch<void>(`/products/${productId}/variants/${variantId}`, { method: "DELETE" }),
  addStock: (productId: number, variantId: number, quantity: number, notes?: string | null) =>
    apiFetch<ProductVariant>(`/products/${productId}/variants/${variantId}/add-stock`, { method: "POST", body: JSON.stringify({ quantity, notes }) }),
};

export const shippingApi = {
  list: () => apiFetch<ShippingCompany[]>("/shipping-companies"),
  create: (data: Partial<ShippingCompany>) => apiFetch<ShippingCompany>("/shipping-companies", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<ShippingCompany>) => apiFetch<ShippingCompany>(`/shipping-companies/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => apiFetch<void>(`/shipping-companies/${id}`, { method: "DELETE" }),
};

const parseFile = async (file: File, endpoint: string): Promise<ParsedImport> => {
  // ── قراءة الملف محلياً بـ SheetJS لتجنب 413 من nginx ──────────────────────
  try {
    const XLSX = await import("xlsx");
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    if (!rows.length) throw new Error("الملف فارغ.");
    const headers = (rows[0] as any[]).map(h => String(h ?? "").trim());
    const dataRows = rows.slice(1).filter(r => (r as any[]).some(c => c !== "" && c != null));
    const sample = dataRows.slice(0, 5);
    return {
      headers,
      sample,
      allRows: dataRows,
      totalRows: dataRows.length,
    } as ParsedImport;
  } catch (localErr: any) {
    // fallback: ارفع الملف للسيرفر (للأنواع غير المدعومة)
    const form = new FormData();
    form.append("file", file);
    const token = getToken();
    const res = await fetch(`${BASE}/${endpoint}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (res.status === 401) {
      localStorage.removeItem("caprina_token");
      localStorage.removeItem("caprina_user");
      window.location.href = "/login";
      throw new Error("غير مصرح");
    }
    if (res.status === 413) throw new Error("الملف كبير جداً. يرجى تقليل حجمه أو تقسيمه إلى أجزاء أصغر.");
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  }
};

const executeImport = async (endpoint: string, payload: { headers: string[]; rows: any[][]; mapping: any }): Promise<ImportResult> => {
  const token = getToken();
  const res = await fetch(`${BASE}/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) {
    localStorage.removeItem("caprina_token");
    localStorage.removeItem("caprina_user");
    window.location.href = "/login";
    throw new Error("غير مصرح");
  }
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
};

export const importApi = {
  // Orders
  parse: (file: File) => parseFile(file, "orders/import/parse"),
  execute: (payload: { headers: string[]; rows: any[][]; mapping: ColumnMapping }) => executeImport("orders/import/execute", payload),
  uploadExcel: async (file: File): Promise<ImportResult> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`${BASE}/orders/import`, { method: "POST", body: form });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },
  // Products
  parseProducts: (file: File) => parseFile(file, "products/import/parse"),
  executeProducts: (payload: { headers: string[]; rows: any[][]; mapping: any }) => executeImport("products/import/execute", payload),
  // Returns
  parseReturns: (file: File) => parseFile(file, "returns/import/parse"),
  executeReturns: (payload: { headers: string[]; rows: any[][]; mapping: any }) => executeImport("returns/import/execute", payload),
  // Inventory bulk update
  uploadInventory: async (file: File): Promise<{ updated: number; failed: number; errors: string[]; items: any[] }> => {
    const form = new FormData();
    form.append("file", file);
    const token = localStorage.getItem("caprina_token");
    const res = await fetch(`${BASE}/import/inventory`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },
};

export interface OrderStats {
  today: { orders: number; revenue: number };
  week: { orders: number; revenue: number };
  month: { orders: number; revenue: number };
  bestProduct: { name: string; quantity: number } | null;
}

export interface PeriodProfit {
  orders: number;
  revenue: number;
  cost: number;
  shippingCost: number;
  netProfit: number;
  returnRate: number;
  returnCount: number;
}

export interface ProductProfit {
  name: string;
  revenue: number;
  cost: number;
  profit: number;
  quantity: number;
  orderCount: number;
  returnCount: number;
  returnRate: number;
  margin: number;
}

export interface ProfitAnalytics {
  today: PeriodProfit;
  week: PeriodProfit;
  month: PeriodProfit;
  allTime: PeriodProfit;
  topProducts: ProductProfit[];
  losingProducts: ProductProfit[];
  inventoryValue: {
    byProduct: number;
    byVariant: number;
    total: number;
    totalUnits: number;
    lowStock: any[];
  };
}

export interface FinancialSummary {
  cashIn: number;
  costOfGoods: number;
  shippingSpend: number;
  grossProfit: number;
  grossMargin: number;
  netProfit: number;
  netMargin: number;
  returnLoss: number;
  returnRevLost: number;
  returnDamagedValue: number;
  pendingRevenue: number;
  returnCount: number;
  returnRate: number;
  totalOrders: number;
  completedOrders: number;
  avgProfitPerOrder: number;
  avgOrderValue: number;
  avgCostPerOrder: number;
  inventoryAtCost: number;
  inventoryAtSell: number;
  potentialInventoryProfit: number;
}

export interface ProductPerformance {
  name: string;
  productId: number | null;
  image: string | null;
  totalOrders: number;
  completedOrders: number;
  totalSalesQty: number;
  totalRevenue: number;
  totalCost: number;
  totalShipping: number;
  returnCount: number;
  returnCostLoss: number;
  netProfit: number;
  avgSalePrice: number;
  margin: number;
  returnRate: number;
  roi: number;
}

export interface ProductPerformanceResponse {
  products: ProductPerformance[];
  byProfit: ProductPerformance[];
  byLoss: ProductPerformance[];
  byReturns: ProductPerformance[];
  summary: {
    totalProducts: number;
    profitableCount: number;
    losingCount: number;
    highReturnCount: number;
    totalNetProfit: number;
    totalRevenue: number;
  };
}

export type AlertType = "HIGH_RETURN" | "LOSING_PRODUCT" | "LOW_STOCK" | "LOW_MARGIN" | "STALE_STOCK" | "NO_COST_DATA";
export type AlertSeverity = "high" | "medium" | "low";

export interface Alert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  detail: string;
  productName?: string;
  value?: number;
}

export interface AlertsResponse {
  alerts: Alert[];
  counts: { total: number; high: number; medium: number; low: number };
}

export type StockCategory = "out" | "fast" | "medium" | "slow" | "stale";

export interface StockIntelligenceItem {
  name: string;
  productId: number | null;
  availableQty: number;
  reservedQty: number;
  soldQty: number;
  costPrice: number;
  unitPrice: number;
  last30DaysSales: number;
  velocityPerDay: number;
  daysUntilStockout: number | null;
  category: StockCategory;
  frozenCapital: number;
  potentialRevenue: number;
}

export interface StockIntelligenceResponse {
  items: StockIntelligenceItem[];
  summary: {
    totalProducts: number;
    fastMovers: number;
    slowMovers: number;
    outOfStock: number;
    totalFrozenCapital: number;
  };
}

// ─── Smart Insights Types ────────────────────────────────────────────────────
export interface AdSourceStat {
  source: string;
  orders: number;
  revenue: number;
  profit: number;
  returnRate: number;
  roi: number;
}

export interface SmartProduct {
  name: string;
  revenue: number;
  cost: number;
  profit: number;
  quantity: number;
  orderCount: number;
  returnCount: number;
  returnRate: number;
  margin: number;
  image?: string | null;
}

export interface DeadStockItem {
  name: string;
  availableQty: number;
  frozenCapital: number;
  last30DaysSales: number;
  daysSinceLastSale: number | null;
  image?: string | null;
}

export interface ReturnReasonItem {
  reason: string;
  label: string;
  count: number;
  pct: number;
}

export interface HighReturnProduct {
  name: string;
  returnRate: number;
  returnCount: number;
  orderCount: number;
}

export interface StockPredictorItem {
  name: string;
  availableQty: number;
  velocityPerDay: number;
  daysUntilStockout: number | null;
  frozenCapital: number;
}

export interface SmartInsights {
  adAttribution: {
    bestSource: AdSourceStat | null;
    breakdown: AdSourceStat[];
  };
  stars: SmartProduct[];
  deadStock: DeadStockItem[];
  returnInsights: {
    byReason: ReturnReasonItem[];
    highReturnProducts: HighReturnProduct[];
    totalReturnRate: number;
    totalReturns: number;
  };
  stockPredictor: StockPredictorItem[];
}

export interface ChartStatusItem { status: string; count: number; pct: number; }
export interface ChartDayItem { date: string; label: string; orders: number; revenue: number; }
export interface ChartSourceItem { source: string; count: number; pct: number; }
export interface WeekComparison {
  thisWeek: { orders: number; revenue: number };
  prevWeek: { orders: number; revenue: number };
  ordersChange: number | null;
  revenueChange: number | null;
  prevWeekDays: ChartDayItem[];
}
export interface ChartsData {
  statusBreakdown: ChartStatusItem[];
  weeklySales: ChartDayItem[];
  monthlySales: ChartDayItem[];
  adSourceBreakdown: ChartSourceItem[];
  total: number;
  weekComparison: WeekComparison;
}

export const analyticsApi = {
  profit: (params?: { period?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.period) q.set("period", params.period);
    if (params?.from)   q.set("from", params.from);
    if (params?.to)     q.set("to", params.to);
    const qs = q.toString();
    return apiFetch<ProfitAnalytics>(`/analytics/profit${qs ? `?${qs}` : ""}`);
  },
  financialSummary: (params?: { period?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.period) q.set("period", params.period);
    if (params?.from)   q.set("from", params.from);
    if (params?.to)     q.set("to", params.to);
    const qs = q.toString();
    return apiFetch<FinancialSummary>(`/analytics/financial-summary${qs ? `?${qs}` : ""}`);
  },
  productPerformance: () => apiFetch<ProductPerformanceResponse>("/analytics/product-performance"),
  alerts: () => apiFetch<AlertsResponse>("/analytics/alerts"),
  stockIntelligence: () => apiFetch<StockIntelligenceResponse>("/analytics/stock-intelligence"),
  smartInsights: () => apiFetch<SmartInsights>("/analytics/smart-insights"),
  shippingFollowup: () => apiFetch<any[]>("/analytics/shipping-followup"),
  charts: () => apiFetch<ChartsData>("/analytics/charts"),
  monthlySales: (month?: string) =>
    apiFetch<{ month: string; days: ChartDayItem[]; totalOrders: number; totalRevenue: number; daysCount: number; avgPerDay: string }>(
      `/analytics/monthly-sales${month ? `?month=${month}` : ""}`
    ),
};

export interface BatchCreateOrderBody {
  invoiceNumber?: string | null;
  customerName: string;
  phone?: string | null;
  city?: string | null;
  address?: string | null;
  zoneId?: number | null;
  shippingCost?: number | null;
  shippingCompanyId?: number | null;
  warehouseId?: number | null;
  assignedUserId?: number | null;
  adSource?: string | null;
  adCampaign?: string | null;
  notes?: string | null;
  orderType?: "normal" | "replacement";
  originalOrderId?: number | null;
  items: {
    product: string;
    color?: string | null;
    size?: string | null;
    quantity: number;
    unitPrice: number;
    costPrice?: number | null;
    productId?: number | null;
    variantId?: number | null;
  }[];
}

export interface BatchCreateOrderResponse {
  invoiceNumber: string;
  orders: any[];
}

export interface OrdersFilterParams {
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  shippingCompanyId?: string;
  city?: string;
  product?: string;
  amountMin?: string;
  amountMax?: string;
}

export interface InventoryShortageCustomer {
  id: number;
  customerName: string;
  phone: string | null;
  quantity: number;
  invoiceNumber: string | null;
  createdAt: string;
}

export interface InventoryShortageItem {
  product: string;
  color: string | null;
  size: string | null;
  totalQty: number;
  orderCount: number;
  customerCount: number;
  customers: InventoryShortageCustomer[];
  unitPrice: number;
  totalRevenue: number;
}

export interface InventoryShortageSummary {
  totalPendingOrders: number;
  totalDistinctProducts: number;
  totalQty: number;
  totalRevenue: number;
}

export interface InventoryShortageResponse {
  items: InventoryShortageItem[];
  summary: InventoryShortageSummary;
}

// مقارنة النواقص بالمخزن
export interface ShortageVsStockItem {
  product: string;
  color: string | null;
  size: string | null;
  needed: number;    // مطلوب من الطلبات المعلقة
  available: number; // متاح في المخزن
  gap: number;       // الكمية الناقصة (0 لو يكفي)
  isCritical: boolean;
  status: "ok" | "partial" | "none"; // ok=يكفي, partial=ناقص جزئي, none=مفيش خالص
}

export interface ShortageVsStockResponse {
  items: ShortageVsStockItem[];
  criticalCount: number;
}

// ─── Feasible Invoices — الفواتير القابلة للتحضير ───────────────────────────
// كل عنصر يمثل فاتورة كاملة (ممكن تحتوي على أكثر من صنف) — الفاتورة تُحضَّر
// بالكامل فقط لو كل أصنافها متوفرة، وإلا تُعتبر الفاتورة كلها معلّقة.
export interface FeasibleInvoiceItem {
  id: number;
  product: string;
  color: string | null;
  size: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  shippingCost: number | null;
  notes: string | null;
  createdAt: string;
}

export interface FeasibleOrder {
  id: number;
  customerName: string;
  phone: string | null;
  product: string;
  color: string | null;
  size: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  shippingCost: number | null;
  invoiceNumber: string | null;
  createdAt: string;
  items: FeasibleInvoiceItem[];
}

export interface MissingItem {
  product: string;
  color: string | null;
  size: string | null;
  needed: number;
  available: number;
  missing: number;
}

export interface SkippedOrder extends FeasibleOrder {
  missing: number;
  reasonAr: string;
  missingItems: MissingItem[];
}

export interface StockUsageItem {
  product: string;
  color: string | null;
  size: string | null;
  wasAvailable: number;
  used: number;
  remaining: number;
}

export interface FeasibleInvoicesSummary {
  feasibleCount: number;
  skippedCount: number;
  totalRevenue: number;
  totalQty: number;
  skippedRevenue: number;
  stockUsage: StockUsageItem[];
}

export interface FeasibleInvoicesResponse {
  feasibleOrders: FeasibleOrder[];
  skippedOrders: SkippedOrder[];
  summary: FeasibleInvoicesSummary;
}

export const ordersApi = {
  stats: () => apiFetch<OrderStats>("/orders/stats"),
  delete: (id: number) => apiFetch<void>(`/orders/${id}`, { method: "DELETE" }),
  archived: () => apiFetch<any[]>("/orders/archived"),
  restore: (id: number) => apiFetch<any>(`/orders/${id}/restore`, { method: "POST" }),
  inManifestIds: () => apiFetch<{ ids: number[] }>("/orders/in-manifest-ids"),
  inventoryShortage: () => apiFetch<InventoryShortageResponse>("/orders/inventory-shortage"),
  shortageVsStock: () => apiFetch<ShortageVsStockResponse>("/orders/shortage-vs-stock"),
  feasibleInvoices: () => apiFetch<FeasibleInvoicesResponse>("/orders/feasible-invoices"),
  bySource: (source: string, dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams({ source });
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    return apiFetch<{ orders: any[]; total: number }>(`/orders/by-source?${params.toString()}`);
  },
  byInvoice: (invoiceNumber: string) =>
    apiFetch<any[]>(`/orders/by-invoice/${encodeURIComponent(invoiceNumber)}`),
  batchCreate: (data: BatchCreateOrderBody) =>
    apiFetch<BatchCreateOrderResponse>("/orders/batch", { method: "POST", body: JSON.stringify(data) }),
  customerLookup: (last4: string) =>
    apiFetch<any[]>(`/orders/customer-lookup?last4=${encodeURIComponent(last4)}`),
  list: (filters?: OrdersFilterParams) => {
    const q = new URLSearchParams();
    if (filters?.search) q.set("search", filters.search);
    if (filters?.status && filters.status !== "all") q.set("status", filters.status);
    if (filters?.dateFrom) q.set("dateFrom", filters.dateFrom);
    if (filters?.dateTo) q.set("dateTo", filters.dateTo);
    if (filters?.shippingCompanyId && filters.shippingCompanyId !== "all") q.set("shippingCompanyId", filters.shippingCompanyId);
    const qs = q.toString();
    return apiFetch<any[]>(`/orders${qs ? `?${qs}` : ""}`);
  },
  getMyOrders: (userId: number, month?: string) => {
    const q = new URLSearchParams();
    q.set("createdByUserId", String(userId));
    if (month) {
      const [y, m] = month.split("-").map(Number);
      const dateFrom = new Date(y, m - 1, 1);
      const dateTo = new Date(y, m, 0);
      q.set("dateFrom", dateFrom.toISOString().split("T")[0]);
      q.set("dateTo", dateTo.toISOString().split("T")[0]);
    }
    return apiFetch<any[]>(`/orders?${q.toString()}`);
  },
};

export type MovementType = "IN" | "OUT";
export type MovementReason = "sale" | "partial_sale" | "return" | "damaged" | "manual_in" | "manual_out" | "adjustment" | "to_shipping" | "from_shipping" | "transfer";

export interface InventoryMovement {
  id: number;
  productId: number | null;
  variantId: number | null;
  warehouseId: number | null;
  warehouseName: string | null;
  product: string;
  color: string | null;
  size: string | null;
  quantity: number;
  type: MovementType;
  reason: MovementReason;
  orderId: number | null;
  fromLocation: string | null;
  toLocation: string | null;
  notes: string | null;
  createdAt: string;
}

export interface MovementFilters {
  type?: MovementType;
  reason?: MovementReason;
  productId?: number;
  warehouseId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export interface MovementTotals {
  totalIn: number;
  totalOut: number;
  balance: number;
  currentStock: number | null; // الرصيد الفعلي من المخزون — بيظهر لما يكون في فلتر على منتج
}

// ─── Shipping Manifests API ─────────────────────────────────────────────────
export type DeliveryStatus = "pending" | "delivered" | "postponed" | "partial_received" | "returned";

export interface ShippingManifestListItem {
  id: number;
  manifestNumber: string;
  shippingCompanyId: number;
  companyName: string;
  status: "open" | "closed";
  notes: string | null;
  invoicePrice: number | null;
  invoiceNotes: string | null;
  orderCount: number;
  postponedCount: number;
  returnedCount: number;
  pendingCount: number;
  createdAt: string;
  closedAt: string | null;
}

export interface ManifestStats {
  total: number;
  delivered: number;
  returned: number;
  partial: number;
  pending: number;
  postponed: number;
  deliveryRate: number;
  totalRevenue: number;
  totalCost: number;
  totalShippingCost: number;
  returnLosses: number;
  netProfit: number;
  deliveredGross: number;
}

export interface ManifestCompanyStats extends ManifestStats {
  manifestCount: number;
}

export interface Order {
  id: number;
  invoiceNumber: string;
  customerName: string;
  customerPhone: string | null;
  city: string | null;
  product: string | null;
  quantity: number | null;
  total: number;
  cost: number | null;
  shippingCost: number | null;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  assignedUserId: number | null;
  createdByUserId: number | null;
  shippingCompanyId: number | null;
}

export interface ManifestOrder extends Order {
  deliveryStatus: DeliveryStatus;
  deliveryNote: string | null;
  deliveredAt: string | null;
  manifestOrderId: number;
  returnReceived: 0 | 1 | null;
  addedAt: string | null;
}

export interface ShippingManifestDetail extends ShippingManifestListItem {
  companyPhone: string | null;
  companyLogo: string | null;
  orders: ManifestOrder[];
  stats: ManifestStats;
  manualShippingCost: number | null;
}

export interface ManifestCloseResponse extends ShippingManifestListItem {
  rolledOverManifest: { id: number; manifestNumber: string; orderCount: number } | null;
}

export const manifestsApi = {
  list: (companyId?: number) =>
    apiFetch<ShippingManifestListItem[]>(`/shipping-manifests${companyId ? `?companyId=${companyId}` : ""}`),
  get: (id: number) =>
    apiFetch<ShippingManifestDetail>(`/shipping-manifests/${id}`),
  create: (data: { shippingCompanyId: number; orderIds: number[]; notes?: string }) =>
    apiFetch<ShippingManifestListItem>("/shipping-manifests", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: { status?: "open" | "closed"; notes?: string; invoicePrice?: number | null; invoiceNotes?: string | null; manualShippingCost?: number | null }) =>
    apiFetch<ManifestCloseResponse>(`/shipping-manifests/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  updateOrderDelivery: (
    manifestId: number,
    orderId: number,
    data: { deliveryStatus: DeliveryStatus; deliveryNote?: string | null; partialQuantity?: number | null; returnReceived?: boolean | null; partialReturnReceived?: boolean | null }
  ) =>
    apiFetch<{ success: boolean; deliveryStatus: DeliveryStatus; deliveryNote: string | null }>(
      `/shipping-manifests/${manifestId}/orders/${orderId}`,
      { method: "PATCH", body: JSON.stringify(data) }
    ),
  cancelOrder: (manifestId: number, orderId: number) =>
    apiFetch<{ success: boolean; orderId: number; message: string }>(
      `/shipping-manifests/${manifestId}/orders/${orderId}`,
      { method: "DELETE" }
    ),
  delete: (id: number) =>
    apiFetch<void>(`/shipping-manifests/${id}`, { method: "DELETE" }),
  addOrders: (manifestId: number, orderIds: number[]) =>
    apiFetch<{ added: number; manifestNumber: string }>(`/shipping-manifests/${manifestId}/orders`, {
      method: "POST", body: JSON.stringify({ orderIds }),
    }),
  companyStats: (companyId: number) =>
    apiFetch<ManifestCompanyStats>(`/shipping-companies/${companyId}/stats`),
  getOrderManifestStatus: (orderId: number) =>
    apiFetch<{
      manifestId: number;
      manifestNumber: string;
      manifestStatus: "open" | "closed";
      deliveryStatus: DeliveryStatus;
      deliveryNote: string | null;
      partialQuantity: number | null;
      deliveredAt: string | null;
      returnReceived: 0 | 1 | null;
    } | null>(`/orders/${orderId}/manifest-status`),
  getInvoiceManifestStatus: (invoiceNumber: string) =>
    apiFetch<Array<{
      orderId: number;
      product: string;
      quantity: number;
      status: string;
      manifestId: number | null;
      manifestNumber: string | null;
      manifestStatus: "open" | "closed" | null;
      deliveryStatus: DeliveryStatus | null;
      deliveryNote: string | null;
      manifestPartialQuantity: number | null;
      deliveredAt: string | null;
      returnReceived: 0 | 1 | null;
    }>>(`/orders/invoice-manifest-status/${encodeURIComponent(invoiceNumber)}`),
};

// ─── Warehouses API ─────────────────────────────────────────────────────────
export interface Warehouse {
  id: number;
  name: string;
  address: string | null;
  notes: string | null;
  isDefault: boolean;
  totalUnits: number;
  skuCount: number;
  orderCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface WarehouseStockItem {
  id: number;
  warehouseId: number;
  productId: number | null;
  variantId: number | null;
  quantity: number;
  productName: string | null;
  productSku: string | null;
  variantColor: string | null;
  variantSize: string | null;
  unitPrice: number | null;
  costPrice: number | null;
  lowStockThreshold: number;
  updatedAt: string;
}

export interface WarehouseDetail extends Warehouse {
  stock: WarehouseStockItem[];
}

export interface VariantWarehouseStock {
  warehouseId: number;
  warehouseName: string;
  isDefault: boolean;
  quantity: number;
}

export const warehousesApi = {
  list: () => apiFetch<Warehouse[]>("/warehouses"),
  get: (id: number) => apiFetch<WarehouseDetail>(`/warehouses/${id}`),
  stockByVariant: (variantId: number) => apiFetch<VariantWarehouseStock[]>(`/warehouses/stock/by-variant/${variantId}`),
  create: (data: { name: string; address?: string | null; notes?: string | null; isDefault?: boolean }) =>
    apiFetch<Warehouse>("/warehouses", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<{ name: string; address: string | null; notes: string | null; isDefault: boolean }>) =>
    apiFetch<Warehouse>(`/warehouses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => apiFetch<void>(`/warehouses/${id}`, { method: "DELETE" }),
  updateStock: (warehouseId: number, stockId: number, quantity: number) =>
    apiFetch<WarehouseStockItem>(`/warehouses/${warehouseId}/stock/${stockId}`, { method: "PATCH", body: JSON.stringify({ quantity }) }),
  addStock: (warehouseId: number, data: { productId?: number | null; variantId?: number | null; quantity: number }) =>
    apiFetch<WarehouseStockItem>(`/warehouses/${warehouseId}/stock`, { method: "POST", body: JSON.stringify(data) }),
};

// ─── Zones API (المناطق) ─────────────────────────────────────────────────────
export interface Zone {
  id: number;
  tenantId: number | null;
  name: string;
  notes: string | null;
  ordersCount: number;
  createdAt: string;
  updatedAt: string;
}

export const zonesApi = {
  list: () => apiFetch<Zone[]>("/zones"),
  create: (data: { name: string; notes?: string | null }) =>
    apiFetch<Zone>("/zones", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: Partial<{ name: string; notes: string | null }>) =>
    apiFetch<Zone>(`/zones/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  delete: (id: number) => apiFetch<void>(`/zones/${id}`, { method: "DELETE" }),
  insights: (params?: { from?: string; to?: string; compare?: boolean }) => {
    const q = new URLSearchParams();
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    if (params?.compare) q.set("compare", "true");
    const qs = q.toString();
    return apiFetch<ZonesInsightsResponse>(`/zones/insights${qs ? `?${qs}` : ""}`);
  },
};

// ─── Zones Insights types (تحليلات المناطق الذكية) ──────────────────────────
export interface ZoneReturnReasonItem {
  reason: string;
  label: string;
  count: number;
  pct: number;
}

export interface ZoneTrend {
  direction: "up" | "down" | "flat";
  revenueTrendPct: number;
  ordersTrendPct: number;
  previousRevenue: number;
  previousOrdersCount: number;
}

export interface ZoneInsight {
  zoneId: string | null;
  zoneName: string;
  ordersCount: number;
  revenue: number;
  deliveredCount: number;
  deliveryRate: number;
  returnedCount: number;
  returnRate: number;
  closedCount: number;
  byReason: ZoneReturnReasonItem[];
  topReason: ZoneReturnReasonItem | null;
  trend?: ZoneTrend;
}

export interface ZonesRiskConcentration {
  topZoneName: string | null;
  topZoneSharePct: number;
  top3ZonesSharePct: number;
  level: "low" | "medium" | "high";
}

export interface ZonesInsightsResponse {
  zones: ZoneInsight[];
  overall: ZoneInsight;
  riskConcentration?: ZonesRiskConcentration;
}

// ─── Team & Campaign Analytics API ──────────────────────────────────────────
export interface TeamMemberStats {
  userId: number;
  userName: string;
  displayName: string;
  avatar: string | null;
  total: number;
  delivered: number;
  returned: number;
  pending: number;
  profit: number;
  deliveryRate: number;
  returnRate: number;
}

export interface TeamMemberExtStats extends TeamMemberStats {
  avgProcessingHours: number | null;
  sourceCounts: Record<string, number>;
  topSource: string | null;
  ordersPerDay: number;
  score: number;
}

export interface CampaignStats {
  adSource: string;
  adCampaign: string | null;
  total: number;
  delivered: number;
  returned: number;
  pending: number;
  revenue: number;
  cost: number;
  profit: number;
  deliveryRate: number;
  roi: number;
}

// ─── Employee Profiles & KPIs ────────────────────────────────────────────────
export interface EmployeeProfile {
  id: number;
  userId: number | null;
  username: string | null;
  displayName: string | null;
  role: string;
  isActive: boolean;
  isSystemUser: boolean;
  jobTitle: string | null;
  department: string | null;
  monthlySalary: number | null;
  hireDate: string | null;
  notes: string | null;
  avatar: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeKpi {
  id: number;
  profileId: number | null;
  userId: number | null;
  name: string;
  metric: string;
  targetValue: number;
  unit: string;
  direction: "higher_is_better" | "lower_is_better";
  weight: number;
  salaryWeight: number;
  overtargetBonus: number;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EvaluatedKpi extends EmployeeKpi {
  actualValue: number | null;
  score: number | null;
  achieved: boolean | null;
}

export interface EmployeeReport {
  userId: number;
  username: string;
  displayName: string;
  role: string;
  profile: EmployeeProfile | null;
  period: { month: string; from: string; to: string };
  orderStats: {
    total: number;
    delivered: number;
    returned: number;
    pending: number;
    deliveryRate: number;
    returnRate: number;
    totalRevenue: number;
    totalProfit: number;
  };
  kpis: EvaluatedKpi[];
  kpiFinancials: {
    totalSalaryWeight: number;
    salaryAtRiskPercent: number;
    totalDeduction: number;
    totalBonus: number;
    achievedCount: number;
    failedCount: number;
    overTargetCount: number;
  };
  overallScore: number | null;
  rating: string;
  salary: number;
}

export interface Attendance {
  id: number;
  profileId: number;
  date: string;         // YYYY-MM-DD
  status: "present" | "absent" | "late" | "half_day" | "holiday" | "excused";
  checkIn: string | null;
  checkOut: string | null;
  checkInPhoto?: string | null;
  checkInLat?: number | null;
  checkInLng?: number | null;
  checkInAddress?: string | null;
  checkOutPhoto?: string | null;
  checkOutLat?: number | null;
  checkOutLng?: number | null;
  checkOutAddress?: string | null;
  lateMinutes: number;
  deduction: number;
  notes: string | null;
}

export interface AttendanceSalaryReport {
  profileId: number | null;
  displayName: string | null;
  noProfile?: boolean;
  month: string;
  baseSalary: number;
  workedDays: number;
  absentDays: number;
  lateDays: number;
  halfDays: number;
  holidayDays: number;
  excusedDays: number;
  totalWorkingDays: number;
  totalRecordedDays?: number;
  workDays?: number;
  attendanceDeduction: number;
  bonuses: number;
  extraDeductions: number;
  netSalary: number;
  attendance: Attendance[];
  adjustments: { id: number; type: "bonus" | "deduction"; amount: number; reason: string }[];
}

export interface DailyKpiEntry extends EmployeeKpi {
  date: string;
  actualValue: number | null;
  dailyTarget: number;
  logId: number | null;
  logNotes: string | null;
  score: number | null;
  achieved: boolean | null;
}

export interface DailyLogDay {
  date: string;
  kpis: DailyKpiEntry[];
}

export interface WeekDay {
  date: string;
  actualValue: number | null;
  dailyTarget: number;
  achieved: boolean | null;
}

export interface KpiWeek {
  kpiId: number;
  kpiName: string;
  days: WeekDay[];
}

export interface WeekLogsResult {
  dates: string[];
  kpiWeeks: KpiWeek[];
}

// ─── Employee Orders (طلبات الموظف) ─────────────────────────────────────────
export interface EmployeeOrderItem {
  id: number;
  invoiceNumber: string | null;
  customerName: string;
  product: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  status: string;
  city: string | null;
  adSource: string | null;
  shippingCost: number | null;
  createdAt: string;
  color: string | null;
  size: string | null;
}

export interface EmployeeOrdersStats {
  total: number;
  delivered: number;
  returned: number;
  inShipping: number;
  pending: number;
  deliveryRate: number;
  returnRate: number;
  totalRevenue: number;
  totalProfit: number;
}

export interface EmployeeOrdersResponse {
  orders: EmployeeOrderItem[];
  stats: EmployeeOrdersStats;
  kpiImpact: {
    deliveryRate: number;
    returnRate: number;
    totalOrders: number;
    revenue: number;
    profit: number;
  };
}

export const employeeApi = {
  listProfiles: () => apiFetch<EmployeeProfile[]>("/employee-profiles"),
  getProfile: (profileId: number) => apiFetch<EmployeeProfile & { kpis: EmployeeKpi[] }>(`/employee-profiles/${profileId}`),
  createProfile: (data: {
    userId?: number;
    displayName?: string;
    jobTitle?: string | null;
    department?: string | null;
    monthlySalary?: number | null;
    hireDate?: string | null;
    notes?: string | null;
    avatar?: string | null;
  }) => apiFetch<EmployeeProfile>("/employee-profiles", { method: "POST", body: JSON.stringify(data) }),
  upsertProfile: (data: {
    userId?: number;
    displayName?: string;
    jobTitle?: string | null;
    department?: string | null;
    monthlySalary?: number | null;
    hireDate?: string | null;
    notes?: string | null;
    avatar?: string | null;
  }) => apiFetch<EmployeeProfile>("/employee-profiles", { method: "POST", body: JSON.stringify(data) }),
  updateProfile: (profileId: number, data: Partial<{
    displayName: string;
    jobTitle: string | null;
    department: string | null;
    monthlySalary: number | null;
    hireDate: string | null;
    notes: string | null;
    avatar: string | null;
  }>) => apiFetch<EmployeeProfile>(`/employee-profiles/${profileId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteProfile: (profileId: number) => apiFetch<void>(`/employee-profiles/${profileId}`, { method: "DELETE" }),
  listKpis: (profileId: number) => apiFetch<EmployeeKpi[]>(`/employee-kpis/${profileId}`),
  createKpi: (data: {
    profileId: number; name: string; metric: string;
    targetValue: number; unit: string;
    direction: "higher_is_better" | "lower_is_better";
    weight: number; salaryWeight?: number; overtargetBonus?: number;
    isActive: boolean; description?: string | null;
  }) => apiFetch<EmployeeKpi>("/employee-kpis", { method: "POST", body: JSON.stringify(data) }),
  updateKpi: (kpiId: number, data: Partial<EmployeeKpi>) =>
    apiFetch<EmployeeKpi>(`/employee-kpis/${kpiId}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteKpi: (kpiId: number) => apiFetch<void>(`/employee-kpis/${kpiId}`, { method: "DELETE" }),
  getReport: (profileId: number, month?: string, mode?: "monthly" | "daily", date?: string) => {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (mode) params.set("mode", mode);
    if (date) params.set("date", date);
    const qs = params.toString();
    return apiFetch<EmployeeReport>(`/analytics/employee-report/${profileId}${qs ? `?${qs}` : ""}`);
  },
  getMyReport: (month?: string, mode?: "monthly" | "daily", date?: string) => {
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (mode)  params.set("mode", mode);
    if (date)  params.set("date", date);
    const qs = params.toString();
    return apiFetch<EmployeeReport>(`/analytics/my-report${qs ? `?${qs}` : ""}`);
  },
  getMyAttendance: (month?: string) =>
    apiFetch<Attendance[]>(`/attendance/my${month ? `?month=${month}` : ""}`),
  getMySalaryReport: (month?: string) =>
    apiFetch<AttendanceSalaryReport>(`/attendance/my/salary-report${month ? `?month=${month}` : ""}`),
  getTodayAttendance: () =>
    apiFetch<(Attendance & { isFlagged?: boolean }) | null>("/attendance/my/today"),
  checkIn: (data?: { photo?: string | null; lat?: number | null; lng?: number | null; address?: string | null }) =>
    apiFetch<Attendance>("/attendance/my/check-in", { method: "POST", body: JSON.stringify(data ?? {}) }),
  checkOut: (data?: { photo?: string | null; lat?: number | null; lng?: number | null; address?: string | null }) =>
    apiFetch<Attendance>("/attendance/my/check-out", { method: "POST", body: JSON.stringify(data ?? {}) }),
  listUsers: () => apiFetch<AppUser[]>("/users"),
  getDailyLogs: (profileId: number, date?: string) =>
    apiFetch<DailyLogDay>(`/employee-daily-logs/${profileId}${date ? `?date=${date}` : ""}`),
  getWeekLogs: (profileId: number, date?: string) =>
    apiFetch<WeekLogsResult>(`/employee-daily-logs/${profileId}/week${date ? `?date=${date}` : ""}`),
  saveDailyLog: (data: { profileId: number; kpiId: number; date: string; value: number; notes?: string | null }) =>
    apiFetch<{ id: number }>("/employee-daily-logs", { method: "POST", body: JSON.stringify(data) }),
  getOrders: (profileId: number, month?: string) =>
    apiFetch<EmployeeOrdersResponse>(`/employee-orders/${profileId}${month ? `?month=${month}` : ""}`),
  getTeamRanking: (month?: string) =>
    apiFetch<any[]>(`/team-ranking${month ? `?month=${month}` : ""}`),
  getStarEmployees: () => apiFetch<any[]>("/star-employees"),
  setStarEmployees: (profileIds: number[]) =>
    apiFetch<{ success: boolean }>("/star-employees", { method: "POST", body: JSON.stringify({ profileIds }) }),
};

export const teamAnalyticsApi = {
  teamPerformance: (dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const qs = params.toString();
    return apiFetch<TeamMemberStats[]>(`/analytics/team-performance${qs ? `?${qs}` : ""}`);
  },
  teamPerformanceExtended: (dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const qs = params.toString();
    return apiFetch<TeamMemberExtStats[]>(`/analytics/team-performance-extended${qs ? `?${qs}` : ""}`);
  },
  campaigns: (dateFrom?: string, dateTo?: string) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    const qs = params.toString();
    return apiFetch<CampaignStats[]>(`/analytics/campaigns${qs ? `?${qs}` : ""}`);
  },
};

// ─── Sessions API ────────────────────────────────────────────────────────────
export interface SessionLog {
  id: number;
  userId: number;
  loginAt: string;
  logoutAt: string | null;
  duration: number | null;
  ipAddress: string | null;
  displayName?: string;
  username?: string;
  role?: string;
}

export interface SessionReport {
  sessions: SessionLog[];
  summary: {
    userId: number;
    displayName: string;
    username: string;
    role: string;
    totalSessions: number;
    totalDuration: number;
    lastLogin: string | null;
  }[];
  period: string;
  from: string;
  to: string;
}

export const sessionsApi = {
  report: (params?: { period?: string; from?: string; to?: string }) => {
    const q = new URLSearchParams();
    if (params?.period) q.set("period", params.period);
    if (params?.from) q.set("from", params.from);
    if (params?.to) q.set("to", params.to);
    return apiFetch<SessionReport>(`/sessions/report?${q.toString()}`);
  },
  me: () => apiFetch<SessionLog[]>("/sessions/me"),
};

export const movementsApi = {
  list: (filters?: MovementFilters) => {
    const params = new URLSearchParams();
    if (filters?.type)        params.set("type",        filters.type);
    if (filters?.reason)      params.set("reason",      filters.reason);
    if (filters?.productId)   params.set("productId",   String(filters.productId));
    if (filters?.warehouseId) params.set("warehouseId", String(filters.warehouseId));
    if (filters?.dateFrom)    params.set("dateFrom",    filters.dateFrom);
    if (filters?.dateTo)      params.set("dateTo",      filters.dateTo);
    const qs = params.toString();
    return apiFetch<InventoryMovement[]>(`/inventory/movements${qs ? `?${qs}` : ""}`);
  },
  totals: (filters?: MovementFilters) => {
    const params = new URLSearchParams();
    if (filters?.type)        params.set("type",        filters.type);
    if (filters?.reason)      params.set("reason",      filters.reason);
    if (filters?.productId != null)   params.set("productId",   String(filters.productId));
    if (filters?.warehouseId != null) params.set("warehouseId", String(filters.warehouseId));
    if (filters?.dateFrom)    params.set("dateFrom",    filters.dateFrom);
    if (filters?.dateTo)      params.set("dateTo",      filters.dateTo);
    const qs = params.toString();
    return apiFetch<MovementTotals>(`/inventory/movements/totals${qs ? `?${qs}` : ""}`);
  },
  create: (data: {
    product: string;
    color?: string | null;
    size?: string | null;
    quantity: number;
    type: MovementType;
    reason: MovementReason;
    productId?: number | null;
    variantId?: number | null;
    warehouseId?: number | null;
    fromLocation?: string | null;
    toLocation?: string | null;
    notes?: string | null;
  }) => apiFetch<InventoryMovement>("/inventory/movements", { method: "POST", body: JSON.stringify(data) }),
  update: (id: number, data: {
    product: string;
    color?: string | null;
    size?: string | null;
    quantity: number;
    type: MovementType;
    reason: MovementReason;
    warehouseId?: number | null;
    fromLocation?: string | null;
    toLocation?: string | null;
    notes?: string | null;
  }) => apiFetch<InventoryMovement>(`/inventory/movements/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  delete: (id: number) => apiFetch<{ success: boolean }>(`/inventory/movements/${id}`, { method: "DELETE" }),
  deleteBulk: (ids: number[]) => apiFetch<{ success: boolean; deleted: number }>("/inventory/movements", { method: "DELETE", body: JSON.stringify({ ids }) }),
};

// ─── App Settings API ─────────────────────────────────────────────────────
export interface AppSettings {
  showAddTeamMember: boolean;
  allowBrandEdit: boolean;
  showTeamPerformance: boolean;
  showTeamManagement: boolean;
  showSmartAnalytics: boolean;
  showAdsAnalytics: boolean;
  showExportData: boolean;
}

export const appSettingsApi = {
  get: () => apiFetch<AppSettings>("/settings"),
  update: (data: Partial<AppSettings>) =>
    apiFetch<AppSettings>("/settings", { method: "PATCH", body: JSON.stringify(data) }),
};

// ─── Attendance & Payroll API ────────────────────────────────────────────────

export type AttendanceStatus = "present" | "absent" | "late" | "half_day" | "holiday" | "excused";

export interface AttendanceRecord {
  id: number;
  profileId: number;
  date: string;
  status: AttendanceStatus;
  checkIn: string | null;
  checkOut: string | null;
  checkInPhoto?: string | null;
  checkInLat?: number | null;
  checkInLng?: number | null;
  checkInAddress?: string | null;
  checkOutPhoto?: string | null;
  checkOutLat?: number | null;
  checkOutLng?: number | null;
  checkOutAddress?: string | null;
  lateMinutes: number;
  deduction: number;
  notes: string | null;
  createdAt: string;
}

export interface PayrollAdjustment {
  id: number;
  profileId: number;
  month: string;
  type: "bonus" | "deduction";
  amount: number;
  reason: string;
  createdAt: string;
}

export interface MonthlySalaryReport {
  profileId: number;
  displayName: string;
  month: string;
  baseSalary: number;
  workedDays: number;
  absentDays: number;
  lateDays: number;
  halfDays: number;
  totalWorkingDays: number;
  attendanceDeduction: number;
  bonuses: number;
  extraDeductions: number;
  netSalary: number;
  attendance: AttendanceRecord[];
  adjustments: PayrollAdjustment[];
}

export const attendanceApi = {
  // جلب سجل الحضور لموظف في شهر معين
  list: (profileId: number, month: string) =>
    apiFetch<AttendanceRecord[]>(`/attendance/${profileId}?month=${month}`),

  // تسجيل أو تعديل يوم حضور
  save: (data: {
    profileId: number;
    date: string;
    status: AttendanceStatus;
    checkIn?: string | null;
    checkOut?: string | null;
    lateMinutes?: number;
    notes?: string | null;
    deduction?: number;
  }) => apiFetch<AttendanceRecord>("/attendance", { method: "POST", body: JSON.stringify(data) }),

  // حذف سجل يوم
  delete: (id: number) => apiFetch<void>(`/attendance/${id}`, { method: "DELETE" }),

  // تقرير المرتب الشهري كامل
  salaryReport: (profileId: number, month: string) =>
    apiFetch<MonthlySalaryReport>(`/attendance/${profileId}/salary-report?month=${month}`),

  // إضافة خصم أو بونص
  addAdjustment: (data: {
    profileId: number;
    month: string;
    type: "bonus" | "deduction";
    amount: number;
    reason: string;
  }) => apiFetch<PayrollAdjustment>("/attendance/adjustments", { method: "POST", body: JSON.stringify(data) }),

  // جلب الخصومات والبونص لشهر
  listAdjustments: (profileId: number, month: string) =>
    apiFetch<PayrollAdjustment[]>(`/attendance/adjustments/${profileId}?month=${month}`),

  // حذف خصم أو بونص
  deleteAdjustment: (id: number) => apiFetch<void>(`/attendance/adjustments/${id}`, { method: "DELETE" }),

  // استثناء/تعديل يوم حضور (عفو عن غياب/تأخير أو خصم يدوي) — يحتاج سبب إجباري
  applyException: (id: number, data: {
    action: "excuse_absence" | "excuse_late" | "custom_deduction";
    reason: string;
    deductionAmount?: number;
  }) => apiFetch<AttendanceRecord>(`/attendance/${id}/exception`, { method: "POST", body: JSON.stringify(data) }),

  // إعداد سماحية التأخير العامة (بالدقايق) — القراءة لأي مستخدم، التعديل سوبر أدمن فقط
  getLateGrace: () => apiFetch<{ graceMinutes: number }>("/attendance/settings/late-grace"),
  setLateGrace: (graceMinutes: number) =>
    apiFetch<{ graceMinutes: number }>("/attendance/settings/late-grace", { method: "PATCH", body: JSON.stringify({ graceMinutes }) }),
};

// ─── Cash Registers API ───────────────────────────────────────────────────────
export type CashRegister = {
  id: number;
  name: string;
  balance: string;
  type: string;
  isDefault: boolean;
};

export const cashRegistersApi = {
  list: () => apiFetch<{ registers: CashRegister[]; totalBalance: number }>("/cash-registers"),
};
